'use client';

import React from 'react';
import { CircularProgress } from 'src/components/PulseUI';
import { useProfile } from 'src/context/providers/ProfileProvider';
import {
  useGetCodeSetsVolumeQuery,
  useGetProviderVolumeQuery,
} from 'src/api/redux';
import { CLAIMS_LABEL, resolveClaimsMetric } from 'src/utils/encounterLabels';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  applyDisplayMode,
  applyCaptureRate,
} from 'src/components/Profiles/shared/widgets/utils';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { EvidenceInfoIcon } from '../PlaysSection/components';
import CaptureRateBadge from 'src/components/Profiles/shared/widgets/CaptureRateBadge';

const CompactTreatingVolumeV2: React.FC = () => {
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget;
  const { entityDetails } = useProfile();
  const entityId = entityDetails?.id;
  const { showEncountersTotals } = useFlags();

  const codeSetIds = [
    ...(widget?.code_sets ?? []),
    ...(widget?.code_set ? [widget.code_set] : []),
  ].map((cs) => cs.id);

  const encounterId = widget?.encounter?.id;
  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const dateRange =
    minYear != null || maxYear != null
      ? { gte: String(minYear), lte: String(maxYear) }
      : undefined;

  const useEncounter = codeSetIds.length === 0 && !!encounterId;

  const { data: codeSetsData, isLoading: codeSetsLoading } =
    useGetCodeSetsVolumeQuery(
      {
        entityId: entityId || '',
        params: {
          code_set_ids: codeSetIds,
          all_code_sets: false,
          ...(minYear != null && { min_year: minYear }),
          ...(maxYear != null && { max_year: maxYear }),
        },
      },
      { skip: !entityId || useEncounter || codeSetIds.length === 0 }
    );

  const { data: encounterData, isLoading: encounterLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        cohort_id: encounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !useEncounter }
    );

  const isLoading = useEncounter ? encounterLoading : codeSetsLoading;

  // Cohort (encounter) slot: the claims-role value reads encounters when flagged, and
  // its label is resolved alongside it so a payload without `encounters` falls back to
  // claims *and* keeps the "Claims" label.
  const encounterMetric = resolveClaimsMetric(
    encounterData,
    true,
    !!showEncountersTotals
  );

  const data = useEncounter
    ? encounterData
      ? {
          total_claims: encounterMetric.value,
          total_patients: encounterData.patients,
        }
      : undefined
    : codeSetsData;

  const displayMode = widget?.display_mode;
  const captureRate = widget?.capture_rate;
  const isMonthlyAvg = displayMode === 'monthly_average';
  const year = maxYear ?? new Date().getFullYear();
  const showPatientsAsHero = widget?.volume_type === 'patients';

  const codeSets = codeSetsData?.code_sets ?? [];

  const claimsLabel = useEncounter ? encounterMetric.label : CLAIMS_LABEL;

  const defaultLabel = showPatientsAsHero ? 'Patients' : claimsLabel;
  const title = isMonthlyAvg
    ? `Monthly ${defaultLabel}`
    : (widget?.label ?? defaultLabel);

  if (isLoading) {
    return (
      <div
        className="flex-1 rounded-xl bg-neutral-50 p-4 flex items-center justify-center min-h-[120px]"
        data-testid="compact-treating-volume-v2-loading"
      >
        <CircularProgress
          size="sm"
          color="neutral"
          aria-label="Loading treating volume"
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="flex-1 rounded-xl bg-neutral-50 p-4"
        data-testid="compact-treating-volume-v2-empty"
      >
        <p className="text-sm font-semibold text-neutral-500">
          No volume data available
        </p>
      </div>
    );
  }

  const claimsValue = handleNumericValue(
    applyCaptureRate(applyDisplayMode(Number(data.total_claims), displayMode), captureRate)
  );
  const patientsValue = handleNumericValue(
    applyCaptureRate(applyDisplayMode(Number(data.total_patients), displayMode), captureRate)
  );
  const heroValue = showPatientsAsHero ? patientsValue : claimsValue;
  const subtitleValue = showPatientsAsHero ? claimsValue : patientsValue;
  const subtitleLabel = showPatientsAsHero ? claimsLabel : 'Patients';

  return (
    <div
      className="rounded-2xl bg-white shadow-sm p-5 flex flex-col gap-1"
      data-testid="compact-treating-volume-v2"
      role="region"
      aria-label={title}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-neutral-500 leading-5">
          {title}
        </p>
        <div className="flex items-center gap-1.5">
          {captureRate != null && <CaptureRateBadge rate={captureRate} />}
          {widget?.evidence && (
            <EvidenceInfoIcon
              evidence={widget.evidence}
              data-testid="compact-treating-volume-v2-evidence-icon"
            />
          )}
        </div>
      </div>
      <p
        className="text-2xl font-bold text-neutral-800 tabular-nums"
        data-testid="compact-treating-volume-v2-hero"
      >
        {heroValue}
      </p>
      <p
        className="text-sm text-neutral-500"
        data-testid="compact-treating-volume-v2-subtitle"
      >
        {subtitleValue} {subtitleLabel} in {year}
      </p>
      {codeSets.length > 0 && (
        <div
          className="flex flex-col gap-1 mt-2 pt-2 border-t border-neutral-100"
          data-testid="compact-treating-volume-v2-breakdown"
        >
          {codeSets.map((cs, index) => {
            const csClaimsValue = handleNumericValue(
              applyCaptureRate(applyDisplayMode(Number(cs.claims), displayMode), captureRate)
            );
            const csPatientsValue = handleNumericValue(
              applyCaptureRate(applyDisplayMode(Number(cs.patients), displayMode), captureRate)
            );
            const csHeroValue = showPatientsAsHero
              ? csPatientsValue
              : csClaimsValue;
            const csHeroLabel = showPatientsAsHero ? 'patients' : 'claims';
            return (
              <div
                key={cs.code_set?.id ?? index}
                className="flex gap-1 items-center justify-between flex-wrap"
                data-testid={`compact-treating-volume-v2-code-set-${cs.code_set?.id}`}
              >
                <span className="text-xs font-medium text-neutral-600 truncate">
                  {cs.code_set?.label ?? `Code Set ${index + 1}`}
                </span>
                <span className="text-xs font-semibold text-neutral-700 tabular-nums whitespace-nowrap">
                  {csHeroValue} {csHeroLabel}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CompactTreatingVolumeV2;
